import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  routeQuickLaunch,
  type QuickLaunchAttachmentMetadata,
  type QuickLaunchTarget,
} from "@/lib/quick-launch-api";
import type { AccessTokenGetter } from "@/lib/utils";
import i18n from "@/lib/i18n";

export type QuickLaunchRoutingStatus =
  | "idle"
  | "routing"
  | "matched"
  | "no_match"
  | "error";

export interface UseQuickLaunchRoutingOptions {
  quickLaunchId: string;
  taskText: string;
  transcript: string;
  attachments: readonly QuickLaunchAttachmentMetadata[];
  getToken: AccessTokenGetter;
  enabled?: boolean;
}

/**
 * Debounced, abortable automatic target selection. Manual choices live beside
 * automatic suggestions rather than replacing them, so a later model response
 * can never silently undo a user's explicit target.
 */
export function useQuickLaunchRouting({
  quickLaunchId,
  taskText,
  transcript,
  attachments,
  getToken,
  enabled = true,
}: UseQuickLaunchRoutingOptions) {
  const [automaticTarget, setAutomaticTarget] = useState<QuickLaunchTarget | null>(null);
  const [manualTarget, setManualTargetState] = useState<QuickLaunchTarget | null>(null);
  const [status, setStatus] = useState<QuickLaunchRoutingStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);
  const sequenceRef = useRef(0);

  // Consumers often derive attachment metadata during render. Snapshot it by
  // value so an otherwise-identical array instance does not continually reset
  // the debounce timer while the dialog is open.
  const attachmentsKey = JSON.stringify(attachments);
  const attachmentSnapshot = useMemo(
    () => attachments.map((attachment) => ({ ...attachment })),
    [attachmentsKey],
  );
  const hasDraft = Boolean(taskText.trim() || transcript.trim() || attachmentSnapshot.length);

  useEffect(() => {
    controllerRef.current?.abort();
    if (!enabled || !quickLaunchId || !hasDraft) {
      if (!hasDraft) setAutomaticTarget(null);
      setStatus("idle");
      setError(null);
      return;
    }

    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    const controller = new AbortController();
    controllerRef.current = controller;
    const timer = window.setTimeout(() => {
      setStatus("routing");
      setError(null);
      void routeQuickLaunch(
        {
          quick_launch_id: quickLaunchId,
          task_text: taskText,
          transcript,
          attachments: attachmentSnapshot,
        },
        getToken,
        controller.signal,
      )
        .then((response) => {
          if (controller.signal.aborted || sequence !== sequenceRef.current) return;
          setAutomaticTarget(response.target);
          setStatus(response.target ? "matched" : "no_match");
        })
        .catch((reason: unknown) => {
          if (controller.signal.aborted || sequence !== sequenceRef.current) return;
          setAutomaticTarget(null);
          setStatus("error");
          setError(reason instanceof Error ? reason.message : i18n.t("quickLaunch.routingFailure"));
        });
    }, 450);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [attachmentSnapshot, enabled, getToken, hasDraft, quickLaunchId, retryNonce, taskText, transcript]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const setManualTarget = useCallback((target: QuickLaunchTarget) => {
    setManualTargetState(target);
  }, []);
  const clearTarget = useCallback(() => {
    // Clearing is an explicit user choice for the current draft. Abort any
    // pending recommendation so a late response cannot immediately repopulate
    // the target that the user just removed; the next draft edit may route
    // normally again.
    sequenceRef.current += 1;
    controllerRef.current?.abort();
    setManualTargetState(null);
    setAutomaticTarget(null);
    setStatus("idle");
    setError(null);
  }, []);
  const useAutomaticSuggestion = useCallback(() => setManualTargetState(null), []);
  const retry = useCallback(() => setRetryNonce((current) => current + 1), []);
  const abort = useCallback(() => controllerRef.current?.abort(), []);

  return {
    target: manualTarget ?? automaticTarget,
    automaticTarget,
    manualTarget,
    selectionSource: manualTarget ? "manual" as const : "automatic" as const,
    status,
    error,
    setManualTarget,
    clearTarget,
    useAutomaticSuggestion,
    retry,
    abort,
  };
}
